<script setup lang="ts">
import { HgIcon, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AgencyAgents from '@/components/AgencyAgents/AgencyAgents.vue';
import { sanitizeInput } from '@/helpers/String';
import { generateAgencyProfilePagePath } from '@/helpers/Url/Url';
import type { Agent } from '@/types';

defineEmits(['agencyCardClick']);

export interface AgencyCardProps {
  customerId: string;
  profilePageUrlKeyWord: string;
  agencyLogo: string;
  agencyName: string;
  postalCode?: string;
  city?: string;
  language: HgSupportedLanguage;
  agents: Agent[];
  propertiesForRentCount: number;
  propertiesForBuyCount: number;
  description?: string;
}

const {
  customerId,
  profilePageUrlKeyWord,
  agencyLogo,
  agencyName,
  postalCode = '',
  city = '',
  language,
  agents,
  description = '',
} = defineProps<AgencyCardProps>();

const imageError = ref(false);
const onImageLoadFailure = () => {
  imageError.value = true;
};

const agencyDescription = sanitizeInput(description).trim();
const agentsWithImage = computed(() => agents.filter((agent) => agent.avatarUrl));

const { t } = useI18n();
</script>

<template>
  <a
    :class="$style.agencyCardWrapper"
    :href="generateAgencyProfilePagePath(language, profilePageUrlKeyWord, customerId)"
    @click="$emit('agencyCardClick')"
  >
    <div :class="$style.agencyCard">
      <div :class="$style.agencyBasicInformation">
        <div :class="$style.agencyLogoWrapper">
          <div :class="$style.agencyLogo">
            <img
              v-if="agencyLogo && !imageError"
              loading="lazy"
              :alt="agencyName"
              :src="agencyLogo"
              @error="onImageLoadFailure"
            />
            <HgIcon v-else size="large" icon="Apartment" :themeClass="$style.agencyCardIcon" />
          </div>
        </div>
        <div :class="$style.agencyContact">
          <div :class="$style.agencyName" :title="agencyName" data-test="agency-name">
            {{ agencyName }}
          </div>
          <div :class="$style.agencyAddress" data-test="agency-address">
            <HgIcon v-if="city" icon="Pin" size="large" :themeClass="$style.agencyCardIcon" />
            <span>{{ postalCode }} {{ city }}</span>
          </div>
        </div>
        <div :class="$style.agencyProperties">
          <div :class="$style.agencyPropertiesRow">
            <div :class="$style.agencyProperty">
              <div :class="$style.saleProperties" data-test="sale-properties">
                {{ propertiesForBuyCount }}
              </div>
              <p>{{ t('agencyList.saleProperties') }}</p>
            </div>
            <div :class="$style.agencyProperty">
              <div :class="$style.rentProperties" data-test="rent-properties">
                {{ propertiesForRentCount }}
              </div>
              <p>{{ t('agencyList.rentProperties') }}</p>
            </div>
          </div>
        </div>
      </div>
      <div v-if="agencyDescription">
        <div :class="$style.agencySubHeadline" data-test="agency-sub-headline">
          {{ t('agencyList.aboutUs') }}
        </div>
        <p :class="$style.agencyDescription" data-test="agency-description">
          {{ agencyDescription }}
        </p>
      </div>
      <AgencyAgents v-if="agentsWithImage.length" :agents="agentsWithImage" />
    </div>
  </a>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.agencyCardIcon {
  @include color-icon($color-grey-70);
}

.agencyCardWrapper {
  display: block;
  border: 1px solid $color-grey-50;
  border-radius: 8px;

  &:hover,
  &:visited,
  &:visited,
  &:active {
    text-decoration: none;
  }

  .agencyCard {
    padding: $space-200;

    @media (max-width: $viewport-sm-min) {
      padding: $space-100;
    }

    .agencyBasicInformation {
      --columns-per-row: 1;

      display: grid;
      grid-template-columns: repeat(var(--columns-per-row), auto);
      gap: $space-100;
      justify-content: flex-start;

      @media (min-width: $viewport-sm-min) {
        grid-template-columns: $space-1000 1fr;
        gap: $space-150;
        justify-content: space-evenly;

        --columns-per-row: 2;
      }

      @media (min-width: $viewport-md-min) {
        grid-template-columns: $space-1000 1fr auto;

        --columns-per-row: 3;
      }
    }

    .agencyLogoWrapper {
      display: flex;
      justify-content: flex-start;
      width: 160px;

      .agencyLogo {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 142px;
        height: 82px;
        padding: 8px;
        border: 1px solid $color-grey-50;
        border-radius: 4px;

        img {
          max-width: 100%;
          height: auto;
          max-height: 82px;
        }
      }
    }

    .saleProperties,
    .rentProperties,
    .agencySubHeadline,
    .agencyName {
      display: -webkit-box;
      margin-top: $space-100;
      margin-bottom: $space-50;
      overflow: hidden;
      font-size: 1.375rem;
      font-weight: 700;
      color: $color-black;
      -webkit-line-clamp: 1;
      word-break: break-all;
      -webkit-box-orient: vertical;

      @media (max-width: $viewport-sm-min) {
        margin-top: 0;
      }
    }

    .agencySubHeadline {
      margin-top: $space-200;
      margin-bottom: 0;

      @include heading-4;

      @media (max-width: $viewport-sm-min) {
        margin-top: $space-50;
      }
    }

    .agencyAddress {
      display: flex;
      align-items: center;

      span {
        margin-left: $space-25;
        color: $color-grey-90;

        @include body-2;
      }
    }

    .agencyProperties {
      .agencyPropertiesRow {
        display: flex;

        .agencyProperty {
          padding: 0 $space-75;
          color: $color-grey-90;

          @media (max-width: $viewport-sm-min) {
            margin-top: $space-50;
          }

          &:first-of-type {
            padding-left: 0;
          }
        }
      }
    }

    .agencyDescription {
      display: -webkit-box;
      margin: 0;
      overflow: hidden;
      color: $color-grey-90;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
    }
  }
}
</style>
